import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

const CURRENCY_RATES = {
  AED: 1,
  USD: 0.27,
  INR: 22.5
};

const CURRENCY_LOCALES = {
  AED: 'en-AE',
  USD: 'en-US',
  INR: 'en-IN'
};

const DEFAULT_CURRENCY = 'AED';

const CurrencyContext = createContext(null);

const formatCurrency = (amount, currency) => {
  const locale = CURRENCY_LOCALES[currency] || 'en-US';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(Number(amount) || 0);
  } catch (error) {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem('currency') || DEFAULT_CURRENCY;
  });

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  const setCurrency = (code) => {
    if (CURRENCY_RATES[code]) {
      setCurrencyState(code);
    }
  };

  const rate = CURRENCY_RATES[currency] || 1;

  const value = useMemo(() => {
    const convertFromBase = (amount) => {
      const num = Number(amount) || 0;
      return num * rate;
    };

    const convertToBase = (amount) => {
      const num = Number(amount) || 0;
      return rate === 0 ? 0 : num / rate;
    };

    const formatBase = (amount) => formatCurrency(convertFromBase(amount), currency);
    const formatDisplay = (amount) => formatCurrency(amount, currency);

    return {
      currency,
      setCurrency,
      rate,
      formatBase,
      formatDisplay,
      convertFromBase,
      convertToBase,
      supportedCurrencies: Object.keys(CURRENCY_RATES),
      baseCurrency: DEFAULT_CURRENCY
    };
  }, [currency, rate]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return ctx;
};

export const currencyRates = CURRENCY_RATES;
